import * as authTypes from '@redux/types/auth';
import omit from 'lodash/omit';
import union from 'lodash/union';
import { combineReducers } from 'redux';
import * as types from '../types';

// ── Unit Actions ──────────────────────────────────────────────────────────────
const unitActionsById = (state: any = {}, action: any) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED:
      return {};
    case types.UNIT_ACTIONS_FETCH_COMPLETED: {
      const { entities, order } = action.payload;
      const newState: any = {};
      order.forEach((id: any) => {
        newState[id] = { isConfirmed: true, ...state[id], ...entities[id] };
      });
      return newState;
    }
    case types.UNIT_ACTIONS_ADD_STARTED: {
      const ua = action.payload;
      return { ...state, [ua.unitActionId]: { isConfirmed: false } };
    }
    case types.UNIT_ACTIONS_ADD_COMPLETED: {
      const { oldId, unitAction } = action.payload;
      const newState = omit(state, oldId);
      newState[unitAction.unitActionId] = { ...state[oldId], ...unitAction, isConfirmed: true };
      return newState;
    }
    case types.UNIT_ACTIONS_ADD_FAILED: {
      const { oldId } = action.payload;
      return omit(state, oldId);
    }
    case types.UNIT_ACTIONS_EDIT_STARTED: {
      const ua = action.payload;
      return { ...state, [ua.unitActionId]: { ...state[ua.unitActionId], oldUnitAction: state[ua.unitActionId], isConfirmed: false } };
    }
    case types.UNIT_ACTIONS_EDIT_COMPLETED: {
      const ua = action.payload;
      return { ...state, [ua.unitActionId]: { ...omit(state[ua.unitActionId], ['oldUnitAction']), ...ua, isConfirmed: true } };
    }
    case types.UNIT_ACTIONS_EDIT_FAILED: {
      const { oldUnitAction } = action.payload;
      return { ...state, [oldUnitAction.unitActionId]: { ...omit(state[oldUnitAction.unitActionId], ['oldUnitAction']), ...oldUnitAction, isConfirmed: true } };
    }
    case types.UNIT_ACTIONS_REMOVE_COMPLETED: {
      const { unitActionId } = action.payload;
      return omit(state, unitActionId);
    }
    default:
      return state;
  }
};

const unitActionsOrder = (state: any[] = [], action: any) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED:
      return [];
    case types.UNIT_ACTIONS_FETCH_COMPLETED:
      return union(action.payload.order);
    case types.UNIT_ACTIONS_ADD_STARTED:
      return [...state, action.payload.unitActionId];
    case types.UNIT_ACTIONS_ADD_COMPLETED: {
      const { oldId, unitAction } = action.payload;
      return state.map((id) => (id === oldId ? unitAction.unitActionId : id));
    }
    case types.UNIT_ACTIONS_ADD_FAILED:
      return state.filter((id) => id !== action.payload.oldId);
    case types.UNIT_ACTIONS_REMOVE_COMPLETED:
      return state.filter((id) => id !== action.payload.unitActionId);
    default:
      return state;
  }
};

const isFetchingUnitActions = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTIONS_FETCH_STARTED: return true;
    case types.UNIT_ACTIONS_FETCH_COMPLETED:
    case types.UNIT_ACTIONS_FETCH_FAILED: return false;
    default: return state;
  }
};
const isAddingUnitAction = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTIONS_ADD_STARTED: return true;
    case types.UNIT_ACTIONS_ADD_COMPLETED:
    case types.UNIT_ACTIONS_ADD_FAILED: return false;
    default: return state;
  }
};
const isEditingUnitAction = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTIONS_EDIT_STARTED: return true;
    case types.UNIT_ACTIONS_EDIT_COMPLETED:
    case types.UNIT_ACTIONS_EDIT_FAILED: return false;
    default: return state;
  }
};
const isRemovingUnitAction = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTIONS_REMOVE_STARTED: return true;
    case types.UNIT_ACTIONS_REMOVE_COMPLETED:
    case types.UNIT_ACTIONS_REMOVE_FAILED: return false;
    default: return state;
  }
};

// ── Unit Action Configurations ────────────────────────────────────────────────
const configsById = (state: any = {}, action: any) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED:
      return {};
    case types.UNIT_ACTION_CONFIGS_FETCH_COMPLETED: {
      const { entities, order } = action.payload;
      const newState: any = {};
      order.forEach((id: any) => {
        newState[id] = { isConfirmed: true, ...state[id], ...entities[id] };
      });
      return newState;
    }
    case types.UNIT_ACTION_CONFIGS_ADD_STARTED: {
      const c = action.payload;
      return { ...state, [c.unitActionConfigurationId]: { isConfirmed: false } };
    }
    case types.UNIT_ACTION_CONFIGS_ADD_COMPLETED: {
      const { oldId, config } = action.payload;
      const newState = omit(state, oldId);
      newState[config.unitActionConfigurationId] = { ...state[oldId], ...config, isConfirmed: true };
      return newState;
    }
    case types.UNIT_ACTION_CONFIGS_ADD_FAILED:
      return omit(state, action.payload.oldId);
    case types.UNIT_ACTION_CONFIGS_EDIT_COMPLETED: {
      const c = action.payload;
      return { ...state, [c.unitActionConfigurationId]: { ...state[c.unitActionConfigurationId], ...c, isConfirmed: true } };
    }
    case types.UNIT_ACTION_CONFIGS_REMOVE_COMPLETED:
      return omit(state, action.payload.unitActionConfigurationId);
    default:
      return state;
  }
};

const configsOrder = (state: any[] = [], action: any) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED:
      return [];
    case types.UNIT_ACTION_CONFIGS_FETCH_COMPLETED:
      return union(action.payload.order);
    case types.UNIT_ACTION_CONFIGS_ADD_STARTED:
      return [...state, action.payload.unitActionConfigurationId];
    case types.UNIT_ACTION_CONFIGS_ADD_COMPLETED: {
      const { oldId, config } = action.payload;
      return state.map((id) => (id === oldId ? config.unitActionConfigurationId : id));
    }
    case types.UNIT_ACTION_CONFIGS_ADD_FAILED:
      return state.filter((id) => id !== action.payload.oldId);
    case types.UNIT_ACTION_CONFIGS_REMOVE_COMPLETED:
      return state.filter((id) => id !== action.payload.unitActionConfigurationId);
    default:
      return state;
  }
};

const isFetchingConfigs = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTION_CONFIGS_FETCH_STARTED: return true;
    case types.UNIT_ACTION_CONFIGS_FETCH_COMPLETED:
    case types.UNIT_ACTION_CONFIGS_FETCH_FAILED: return false;
    default: return state;
  }
};
const isAddingConfig = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTION_CONFIGS_ADD_STARTED: return true;
    case types.UNIT_ACTION_CONFIGS_ADD_COMPLETED:
    case types.UNIT_ACTION_CONFIGS_ADD_FAILED: return false;
    default: return state;
  }
};
const isEditingConfig = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTION_CONFIGS_EDIT_STARTED: return true;
    case types.UNIT_ACTION_CONFIGS_EDIT_COMPLETED:
    case types.UNIT_ACTION_CONFIGS_EDIT_FAILED: return false;
    default: return state;
  }
};
const isRemovingConfig = (state = false, action: any) => {
  switch (action.type) {
    case types.UNIT_ACTION_CONFIGS_REMOVE_STARTED: return true;
    case types.UNIT_ACTION_CONFIGS_REMOVE_COMPLETED:
    case types.UNIT_ACTION_CONFIGS_REMOVE_FAILED: return false;
    default: return state;
  }
};


const unitActionPermissions = (state: any[] = [], action: any) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: return [];
    case types.PERMISSIONS_FETCH_COMPLETED: return action.payload.permissions;
    default: return state;
  }
};

const isFetchingUnitActionPermissions = (state = false, action: any) => {
  switch (action.type) {
    case types.PERMISSIONS_FETCH_STARTED: return true;
    case types.PERMISSIONS_FETCH_COMPLETED:
    case types.PERMISSIONS_FETCH_FAILED: return false;
    default: return state;
  }
};

// ── Combined ──────────────────────────────────────────────────────────────────
export default combineReducers({
  unitActionsById,
  unitActionsOrder,
  isFetchingUnitActions,
  isAddingUnitAction,
  isEditingUnitAction,
  isRemovingUnitAction,
  configsById,
  configsOrder,
  isFetchingConfigs,
  isAddingConfig,
  isEditingConfig,
  isRemovingConfig,
  unitActionPermissions,
  isFetchingUnitActionPermissions
});

// ── Selectors ─────────────────────────────────────────────────────────────────
export const getOwnState = (state: any) => state.unitActions;

export const getUnitAction = (state: any, id: number) => getOwnState(state).unitActionsById[id];
export const getUnitActionsList = (state: any) =>
  getOwnState(state).unitActionsOrder.map((id: number) => getUnitAction(state, id));

export const getUnitActionConfiguration = (state: any, id: number) => getOwnState(state).configsById[id];
export const getUnitActionConfigurationsList = (state: any) =>
  getOwnState(state).configsOrder.map((id: number) => getUnitActionConfiguration(state, id));

export const getUnitActionPermissionsList = (state: any) => getOwnState(state).unitActionPermissions;

export const isFetchingListUnitActions = (state: any) => getOwnState(state).isFetchingUnitActions;
export const isAddingUnitActionSelector = (state: any) => getOwnState(state).isAddingUnitAction;
export const isEditingUnitActionSelector = (state: any) => getOwnState(state).isEditingUnitAction;
export const isRemovingUnitActionSelector = (state: any) => getOwnState(state).isRemovingUnitAction;

export const isFetchingListConfigs = (state: any) => getOwnState(state).isFetchingConfigs;
export const isAddingConfigSelector = (state: any) => getOwnState(state).isAddingConfig;
export const isEditingConfigSelector = (state: any) => getOwnState(state).isEditingConfig;
export const isRemovingConfigSelector = (state: any) => getOwnState(state).isRemovingConfig;
